/**
 * Listing order for the aid back-office, mirroring the Django table's columns.
 *
 * The Django list sorts on a column-header click (`django_tables2`); a card list
 * has no headers, so the SPA offers the same columns as a picker. Both must land
 * on the same order for the same choice, which is why this lives apart from the
 * component and is tested directly: the interesting parts (an aid with no
 * eligibility date, the status ordering, the tie-break) are exactly the ones a
 * rendering test would express least clearly.
 */
import type { FinancialAidAdmin, FinancialAidStatus } from "../../types";

/** The orderable columns of the Django table, in its header order. */
export const SORT_KEYS = [
  "default",
  "title",
  "organization",
  "eligibility",
  "status",
  "actions",
] as const;
export type SortKey = (typeof SORT_KEYS)[number];

// FinancialAidStatus.CHOICES: declaration order *is* the urgency order the
// `status_rank` annotation gives the Django column (financial_aids/status.py).
const STATUS_ORDER: FinancialAidStatus[] = [
  "pending",
  "unassociated",
  "inactive",
  "expired",
  "upcoming",
  "active",
];

/**
 * Each key mirrors what the matching Django column orders on, ties included: the
 * table's `order_by` tuples end in `title`, and a `-` prefix flips *every*
 * element — so the direction is applied once, to the whole comparison.
 */
const KEYS: Record<SortKey, (aid: FinancialAidAdmin) => (string | number)[]> = {
  default: () => [],
  title: (aid) => [aid.title],
  organization: (aid) => [aid.organization, aid.title],
  // `status_rank`, not the translated label: the Django column orders by urgency
  // (pending → active), which alphabetical order matches in no language and which
  // would re-shuffle the list when the UI language changes.
  status: (aid) => [STATUS_ORDER.indexOf(aid.status), aid.title],
  // The window's opening date then its end, like the Django column. An aid open
  // "en tout temps" carries neither.
  eligibility: (aid) => [
    aid.eligibility_start ?? "",
    aid.eligibility_end ?? "",
    aid.title,
  ],
  actions: (aid) => [aid.action_link_count ?? 0, aid.title],
};

/**
 * Compare two sort keys element by element, as Postgres compares an `ORDER BY`
 * tuple. An empty string sorts *after* a value, matching the NULLS LAST that
 * Django's ascending order gives an aid with no eligibility date — and since the
 * caller applies the direction to the whole result, descending flips that too,
 * exactly as `?sort=-eligibility` does.
 */
export function compareKeys(left: (string | number)[], right: (string | number)[]) {
  for (let i = 0; i < left.length; i += 1) {
    const a = left[i];
    const b = right[i];
    if (a === b) continue;
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (a === "") return 1;
    if (b === "") return -1;
    const compared = String(a).localeCompare(String(b), undefined, { numeric: true });
    if (compared !== 0) return compared;
  }
  return 0;
}

/**
 * Sort a copy of `aids`. `"default"` keeps the API's order (alphabetical by
 * title) — the same list the Django table opens on — so it is returned untouched.
 */
export function sortAids(
  aids: FinancialAidAdmin[],
  sort: SortKey,
  descending: boolean,
): FinancialAidAdmin[] {
  if (sort === "default") return aids;
  const dir = descending ? -1 : 1;
  const key = KEYS[sort];
  return [...aids].sort((a, b) => dir * compareKeys(key(a), key(b)));
}
